// Architecture catalogue for AIDAN (Automated Index for Data Asset Nomenclature).
// Reverse-engineered from the live codebase: Cloudflare Workers + Hono API,
// React 18 SPA, D1 (SQLite at the edge), KV cache, R2 blob store.
//
// Consumed by the React About page; mermaid strings are rendered client-side
// via a dynamic mermaid@10 import. Keep mermaid syntax conservative.

/* eslint-disable max-lines */

export interface DiagramSpec {
  id: string;
  title: string;
  caption: string;
  /** Full mermaid source. (Field is named `source` to match the MermaidDiagram component prop.) */
  source: string;
  /** Optional free-form note rendered under the diagram. */
  notes?: string;
}

export interface ComponentSpec {
  name: string;
  layer: "presentation" | "application" | "domain" | "data" | "platform";
  technology: string;
  responsibilities: string[];
  dependencies: string[];
  files: string[];
}

export type Cardinality = "1:1" | "1:N" | "N:1" | "N:N";

export interface DataEntity {
  /** Stable id used by the consumer to key cards and resolve relationships. */
  id: string;
  name: string;
  table: string;
  description: string;
  primaryKey: string;
  fields: Array<{ name: string; type: string; notes?: string }>;
  relationships: Array<{ to: string; kind: Cardinality; via?: string }>;
  indexes: string[];
}

export interface TechChoice {
  id: string;
  category: string;
  choice: string;
  rationale: string;
  alternatives: string[];
}

export interface RouteEntry {
  method: string;
  path: string;
  resource: string;
  handler: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Diagrams
// ---------------------------------------------------------------------------

const systemContextMermaid = `flowchart LR
  classDef actor fill:#e3f2fd,stroke:#1565c0,color:#0d47a1
  classDef system fill:#fff3e0,stroke:#ef6c00,color:#e65100
  classDef aidan fill:#e8f5e9,stroke:#2e7d32,color:#1b5e20,stroke-width:2px
  classDef ext fill:#f3e5f5,stroke:#6a1b9a,color:#4a148c

  steward([Data Steward]):::actor
  auditor([Auditor / Compliance]):::actor
  engineer([Platform Engineer]):::actor
  consumer([Downstream App / API client]):::actor

  subgraph AIDAN["AIDAN — Reference Data Management"]
    direction TB
    spa[React SPA]
    api[Hono REST API]
    db[(D1 SQLite)]
  end
  class AIDAN aidan

  bbg[Bloomberg / Market Data]:::ext
  sap[SAP / ERP]:::ext
  snow[Snowflake / Lakehouse]:::ext
  idp[Identity Provider]:::ext

  steward -- "browse, propose, approve" --> AIDAN
  auditor -- "review audit log, lineage" --> AIDAN
  engineer -- "API keys, webhooks, imports" --> AIDAN
  consumer -- "read REST / receive webhooks" --> AIDAN

  AIDAN <-- "feeds / crosswalks" --> bbg
  AIDAN <-- "master data sync" --> sap
  AIDAN -- "publish gold records" --> snow
  AIDAN -- "auth (JWT / header)" --> idp`;

const containersMermaid = `flowchart TB
  classDef edge fill:#e8eaf6,stroke:#283593,color:#1a237e
  classDef storage fill:#fff8e1,stroke:#f9a825,color:#f57f17
  classDef client fill:#e0f7fa,stroke:#00838f,color:#006064

  browser["Browser (Chromium / Firefox / Safari)<br/>React 18 SPA"]:::client

  subgraph CF["Cloudflare Edge — Worker Runtime (V8 isolates)"]
    direction TB
    assets["ASSETS binding<br/>Static SPA (dist/)"]
    worker["Worker (Hono app)<br/>worker/index.ts"]
    mw["Middleware<br/>auth · errors · CORS · secure-headers · logger"]
  end
  class CF edge

  d1[("D1: rdm-d1<br/>SQLite, FTS5")]:::storage
  kv[("KV: CACHE<br/>prefs, settings, cache")]:::storage
  r2[("R2: rdm-files<br/>import blobs, exports")]:::storage

  domain["rdm.davicity.com.au<br/>(custom domain) + workers.dev fallback"]

  browser -- "HTTPS" --> domain
  domain --> assets
  domain --> worker
  worker --> mw
  worker -- "SQL via env.DB" --> d1
  worker -- "get/put via env.CACHE" --> kv
  worker -- "stream via env.FILES" --> r2
  assets -- "SPA fallback /*" --> browser`;

const componentSpaMermaid = `flowchart LR
  classDef page fill:#e8f5e9,stroke:#2e7d32
  classDef comp fill:#e3f2fd,stroke:#1565c0
  classDef store fill:#fff3e0,stroke:#ef6c00
  classDef lib fill:#f3e5f5,stroke:#6a1b9a

  subgraph Routing["react-router-dom v6"]
    router[router.tsx<br/>createBrowserRouter]
  end

  subgraph Pages["Pages (src/pages/*)"]
    dash[Dashboard]:::page
    browse[Browse + RecordDetail]:::page
    appr[Approvals]:::page
    lin[Lineage]:::page
    ver[Versions]:::page
    hier[Hierarchies]:::page
    map[Mappings]:::page
    imp[Imports]:::page
    qual[Quality]:::page
    intg[ApiKeys / Integrations]:::page
    aud[AuditLog]:::page
    setg[Settings]:::page
  end

  subgraph Shell["AppShell + Navigation"]
    shell[AppShell]:::comp
    cmd[Command palette]:::comp
    badges[Pending badges]:::comp
  end

  subgraph Components["src/components/*"]
    grid[grid/ — virtualised table<br/>@tanstack/react-virtual]:::comp
    prim[primitives/]:::comp
    a11y[a11y/ helpers]:::comp
    dom[domain/ widgets]:::comp
  end

  subgraph Stores["Zustand stores (src/store/*)"]
    auth[auth]:::store
    badge[badges]:::store
    brstore[browse]:::store
    setgs[settings]:::store
    ui[ui]:::store
  end

  subgraph Lib["src/lib"]
    api[api.ts<br/>typed fetch wrapper]:::lib
    types[types.ts]:::lib
  end

  router --> shell
  shell --> Pages
  Pages --> Components
  Pages --> Stores
  Pages --> api
  api -- "fetch /api/*" --> worker[(Worker)]
  api -- "dispatch aidan:mutation" --> Stores`;

const componentWorkerMermaid = `flowchart TB
  classDef route fill:#e3f2fd,stroke:#1565c0
  classDef mw fill:#fff3e0,stroke:#ef6c00
  classDef bind fill:#fff8e1,stroke:#f9a825
  classDef core fill:#e8f5e9,stroke:#2e7d32,stroke-width:2px

  entry[worker/index.ts<br/>Hono app]:::core

  subgraph MW["Middleware (worker/middleware)"]
    logger[hono/logger]:::mw
    sec[hono/secure-headers]:::mw
    cors[hono/cors]:::mw
    auth[auth.ts<br/>session + api-key]:::mw
    err[error.ts]:::mw
  end

  subgraph Routes["Feature routers (worker/routes)"]
    direction LR
    r1[domains]:::route
    r2[records]:::route
    r3[versions]:::route
    r4[approvals]:::route
    r5[hierarchies]:::route
    r6[mappings]:::route
    r7[lineage]:::route
    r8[imports]:::route
    r9[quality]:::route
    r10[integrations]:::route
    r11[audit]:::route
    r12[search]:::route
    r13[settings]:::route
    r14[stats]:::route
  end

  subgraph Lib["worker/lib"]
    env[env.ts]
    dbh[db.ts helpers]
  end

  subgraph Bindings["Env bindings (wrangler.toml)"]
    db[(D1: DB)]:::bind
    kv[(KV: CACHE)]:::bind
    r2[(R2: FILES)]:::bind
    asts[ASSETS]:::bind
  end

  entry --> MW
  entry --> Routes
  Routes --> Lib
  Lib --> Bindings
  Routes --> Bindings`;

const requestFlowMermaid = `sequenceDiagram
  autonumber
  participant U as User Browser
  participant CF as Cloudflare Edge
  participant W as Worker (Hono)
  participant A as auth middleware
  participant R as Route handler
  participant D as D1 (SQLite)
  participant K as KV
  participant S as R2

  U->>CF: GET https://rdm.davicity.com.au/api/domains
  CF->>W: dispatch /api/*
  W->>A: enrich ctx.user (cookie / X-Api-Key)
  A-->>W: user attached or 401
  W->>R: route match -> domainsRoutes
  R->>K: optional cache lookup
  R->>D: SELECT ... FROM domains
  D-->>R: rows
  R-->>W: c.json(rows)
  W-->>CF: 200 + secure-headers
  CF-->>U: JSON

  Note over U,W: Mutations (POST/PATCH/DELETE) also write record_versions and audit_events in the same txn

  U->>CF: GET /browse/instruments
  CF->>W: notFound -> ASSETS.fetch
  W-->>CF: index.html (SPA shell)
  CF-->>U: HTML, hydrate React`;

const deploymentMermaid = `flowchart TB
  classDef pop fill:#e3f2fd,stroke:#1565c0
  classDef store fill:#fff8e1,stroke:#f9a825
  classDef dns fill:#f3e5f5,stroke:#6a1b9a

  subgraph DNS["Cloudflare DNS + Zone"]
    dom[rdm.davicity.com.au<br/>custom_domain = true]:::dns
    fb[rdm.acct.workers.dev<br/>fallback]:::dns
  end

  subgraph Global["Cloudflare Global Network — 300+ PoPs"]
    direction LR
    syd[SYD edge]:::pop
    sin[SIN edge]:::pop
    lhr[LHR edge]:::pop
    iad[IAD edge]:::pop
    other[... 300+ PoPs ...]:::pop
  end

  subgraph Data["Stateful primitives"]
    d1[(D1: rdm-d1<br/>primary region: APAC/Sydney)]:::store
    kv[(KV: CACHE<br/>eventually consistent, global)]:::store
    r2[(R2: rdm-files<br/>multi-region object store)]:::store
  end

  DNS --> Global
  syd --> d1
  sin --> d1
  lhr --> d1
  iad --> d1
  Global --> kv
  Global --> r2`;

const dataModelMermaid = `erDiagram
  DOMAINS ||--o{ DOMAIN_FIELDS : "defines schema"
  DOMAINS ||--o{ RECORDS : "owns"
  RECORDS ||--o{ RECORD_VERSIONS : "history"
  RECORDS ||--o{ APPROVALS : "pending change"
  APPROVALS ||--o{ APPROVAL_COMMENTS : "discussion"
  DOMAINS ||--o{ HIERARCHIES : "categorise"
  HIERARCHIES ||--o{ HIERARCHY_NODES : "tree"
  HIERARCHY_NODES ||--o{ HIERARCHY_NODES : "parent_id"
  DOMAINS ||--o{ MAPPING_SETS : "source-or-target"
  MAPPING_SETS ||--o{ MAPPING_ITEMS : "crosswalk rows"
  LINEAGE_NODES ||--o{ LINEAGE_EDGES : "from"
  LINEAGE_NODES ||--o{ LINEAGE_EDGES : "to"
  DOMAINS ||--o{ QUALITY_RULES : "validates"
  QUALITY_RULES ||--o{ QUALITY_RESULTS : "evaluated"
  RECORDS ||--o{ QUALITY_RESULTS : "scored"
  DOMAINS ||--o{ IMPORT_JOBS : "ingest into"
  USERS ||--o{ USER_ROLES : "assigned"
  ROLES ||--o{ USER_ROLES : "granted to"
  USERS ||--o{ API_KEYS : "creates"
  USERS ||--o{ AUDIT_EVENTS : "actor"
  RECORDS ||--o{ RECORDS_FTS : "indexed by"

  DOMAINS {
    string id PK
    string name
    string description
    string icon
    string owner
    string primary_key
    int schema_version
    string created_at
    string updated_at
  }
  DOMAIN_FIELDS {
    string domain_id PK
    string field_key PK
    string label
    string type
    int required
    string enum_values
    string ref_domain
    int sort_order
    int searchable
  }
  RECORDS {
    string domain_id PK
    string id PK
    int version
    string status
    string data
    int quality_score
    string effective_from
    string effective_to
    string tags
    string created_at
    string created_by
    string updated_at
    string updated_by
  }
  RECORD_VERSIONS {
    string domain_id PK
    string record_id PK
    int version PK
    string data
    string diff
    string changed_by
    string changed_at
    string approval_id
    string comment
  }
  APPROVALS {
    string id PK
    string record_id FK
    string domain_id FK
    string change_summary
    string diff
    string proposed_data
    string maker
    string maker_at
    string checker
    string checked_at
    string status
    string priority
    int impact_count
  }
  APPROVAL_COMMENTS {
    string id PK
    string approval_id FK
    string author
    string at
    string body
  }
  HIERARCHIES {
    string id PK
    string domain_id FK
    string name
    string description
    string created_at
    string updated_at
  }
  HIERARCHY_NODES {
    string hierarchy_id PK
    string id PK
    string parent_id FK
    string label
    int record_count
    string badge
    int sort_order
  }
  MAPPING_SETS {
    string id PK
    string name
    string source_domain_id FK
    string target_domain_id FK
    string description
    string created_at
    string last_synced_at
  }
  MAPPING_ITEMS {
    string id PK
    string set_id FK
    string source_id
    string target_id
    real confidence
    int verified
    string verified_by
    string notes
  }
  LINEAGE_NODES {
    string id PK
    string kind
    string label
    string domain
  }
  LINEAGE_EDGES {
    string from_id PK
    string to_id PK
    string kind PK
    real confidence
  }
  QUALITY_RULES {
    string id PK
    string domain_id FK
    string name
    string description
    string expression
    string severity
    int enabled
    string created_at
  }
  QUALITY_RESULTS {
    string rule_id PK
    string record_id PK
    string domain_id FK
    int passed
    string message
    string evaluated_at
  }
  IMPORT_JOBS {
    string id PK
    string domain_id FK
    string filename
    string r2_key
    string status
    string column_mapping
    int rows_total
    int rows_ok
    int rows_error
    string error_report_key
    string created_by
    string created_at
  }
  API_KEYS {
    string id PK
    string name
    string prefix
    string hash
    string scopes
    string created_by FK
    string created_at
    string last_used_at
    string expires_at
    int revoked
  }
  WEBHOOKS {
    string id PK
    string url
    string events
    string secret_prefix
    string secret_hash
    int enabled
    string last_delivery_at
    int last_status
    string created_at
  }
  INTEGRATIONS {
    string id PK
    string name
    string kind
    string status
    string domain
    int tps
    string sla
    string metadata
  }
  AUDIT_EVENTS {
    string id PK
    string at
    string actor FK
    string action
    string target
    string diff
    string ip
    string user_agent
  }
  USERS {
    string id PK
    string email
    string name
    string avatar_url
    string created_at
    string last_login
  }
  ROLES {
    string id PK
    string name
    string capabilities
  }
  USER_ROLES {
    string user_id PK
    string role_id PK
  }
  RECORDS_FTS {
    string domain_id
    string id
    string label
    string body
  }`;

const approvalsFlowMermaid = `stateDiagram-v2
  [*] --> pending : maker submits change
  pending --> approved : checker /approve (different user)
  pending --> rejected : checker /reject (+ comment)
  pending --> withdrawn : maker /withdraw
  pending --> expired : TTL exceeded (sweep)
  approved --> [*] : record + record_version written, audit emitted
  rejected --> [*] : no record mutation
  withdrawn --> [*]
  expired --> [*]

  note right of pending
    Maker must differ from Checker.
    bulk-approve fans out to /approve.
    Comments stored in approval_comments.
  end note`;

const importFlowMermaid = `sequenceDiagram
  autonumber
  participant U as User
  participant SPA as React SPA
  participant API as Worker /api/imports
  participant R2 as R2 (FILES)
  participant D1 as D1 (DB)

  U->>SPA: drop CSV/XLSX
  SPA->>API: POST /api/imports (multipart)
  API->>R2: PUT file stream (r2Key)
  API->>D1: INSERT import_jobs (status=uploaded)
  API-->>SPA: ImportJob {id, columns}

  SPA->>U: column-mapping UI + preview
  U->>SPA: confirm mapping
  SPA->>API: POST /api/imports/:id/confirm {mapping}
  API->>R2: GET file
  API->>D1: BEGIN; INSERT records (batched); INSERT record_versions; UPDATE import_jobs
  API->>D1: write audit_events
  API-->>SPA: ImportJob {status=done, ok/err counts}

  Note over SPA: aidan:mutation event fires - stores refresh`;

const liveUpdatesMermaid = `sequenceDiagram
  participant U as User Action
  participant API as api.ts (fetch wrapper)
  participant W as Worker
  participant Win as window
  participant S as Zustand stores
  participant Page as Active page

  U->>API: api.records.update(...)
  API->>W: PATCH /api/domains/:id/records/:rid
  W-->>API: 200 + new RecordRow
  API->>Win: dispatchEvent('aidan:mutation', {path, method})
  Win-->>S: badges/browse/ui listeners
  S-->>Page: re-render with fresh counts
  Note over API,S: Mutations (POST/PATCH/PUT/DELETE) broadcast; GETs do not.`;

export const diagrams: Record<string, DiagramSpec> = {
  systemContext: {
    id: "systemContext",
    title: "C4 L1 — System Context",
    caption:
      "AIDAN sits between human stewards/auditors and the upstream/downstream systems that feed and consume reference data.",
    source: systemContextMermaid,
    notes: "External systems (Bloomberg, SAP, Snowflake) are illustrative — real integrations are configured per tenant via /integrations. The Identity Provider is pluggable; the worker auth middleware accepts session cookies or X-Api-Key headers.",
  },
  containers: {
    id: "containers",
    title: "C4 L2 — Containers",
    caption: "A single Cloudflare Worker serves both the SPA assets and the REST API, backed by D1, KV and R2.",
    source: containersMermaid,
  },
  componentSpa: {
    id: "componentSpa",
    title: "C4 L3 — SPA Components",
    caption: "Pages compose primitives, talk to the API via a typed fetch wrapper, and share state through Zustand stores.",
    source: componentSpaMermaid,
  },
  componentWorker: {
    id: "componentWorker",
    title: "C4 L3 — Worker Components",
    caption: "Hono mounts 14 feature routers behind a small middleware stack; all I/O goes through env bindings.",
    source: componentWorkerMermaid,
  },
  requestFlow: {
    id: "requestFlow",
    title: "Request Flow",
    caption: "API requests run inside a V8 isolate at the edge; SPA navigation falls through to the ASSETS binding.",
    source: requestFlowMermaid,
  },
  deployment: {
    id: "deployment",
    title: "Deployment Topology",
    caption: "Code is replicated globally; D1 keeps its primary in APAC (Sydney), reads can serve from any PoP.",
    source: deploymentMermaid,
  },
  dataModel: {
    id: "dataModel",
    title: "Data Model (D1)",
    caption: "Full ER diagram of all D1 tables — domains/records are the hub, with versioning, approvals, lineage and quality as satellites.",
    source: dataModelMermaid,
  },
  approvalsFlow: {
    id: "approvalsFlow",
    title: "Approvals State Machine",
    caption: "Two-eyes (maker/checker) workflow; only approved transitions mutate the record.",
    source: approvalsFlowMermaid,
  },
  importFlow: {
    id: "importFlow",
    title: "Import Flow",
    caption: "Uploads land in R2 first; commit reads the blob back and writes records + versions transactionally.",
    source: importFlowMermaid,
  },
  liveUpdates: {
    id: "liveUpdates",
    title: "Live UI Updates (aidan:mutation)",
    caption: "The fetch wrapper broadcasts a CustomEvent after every mutation; stores listen and refresh.",
    source: liveUpdatesMermaid,
  },
};

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

export const components: ComponentSpec[] = [
  {
    name: "React SPA Shell",
    layer: "presentation",
    technology: "React 18 + react-router-dom v6",
    responsibilities: [
      "Top-level routing via createBrowserRouter",
      "AppShell layout, navigation, command palette",
      "Mounts feature pages under /home, /browse, /approvals, etc.",
    ],
    dependencies: ["Pages", "Navigation"],
    files: ["src/router.tsx", "src/components/navigation/AppShell.tsx"],
  },
  {
    name: "Pages",
    layer: "presentation",
    technology: "React function components",
    responsibilities: [
      "Dashboard, Browse, RecordDetail, Approvals, Lineage, Versions, Hierarchies, Mappings, Imports, Quality, ApiKeys, AuditLog, Settings",
      "Compose grid + primitives + domain widgets",
    ],
    dependencies: ["API Client", "Zustand stores", "Grid", "Primitives"],
    files: ["src/pages/*.tsx"],
  },
  {
    name: "Grid (Virtualised Table)",
    layer: "presentation",
    technology: "@tanstack/react-virtual",
    responsibilities: ["Render large record lists efficiently", "Column resizing, sorting, selection"],
    dependencies: [],
    files: ["src/components/grid/"],
  },
  {
    name: "Primitives & A11y",
    layer: "presentation",
    technology: "TypeScript + CSS modules",
    responsibilities: ["Buttons, dialogs, menus, focus traps, ARIA helpers"],
    dependencies: [],
    files: ["src/components/primitives/", "src/components/a11y/"],
  },
  {
    name: "Zustand Stores",
    layer: "application",
    technology: "zustand@4",
    responsibilities: [
      "auth: current user, capabilities",
      "badges: pending approval counters",
      "browse: per-domain table state",
      "settings: theme, density, prefs",
      "ui: command palette, toasts",
    ],
    dependencies: ["API Client"],
    files: ["src/store/auth.ts", "src/store/badges.ts", "src/store/browse.ts", "src/store/settings.ts", "src/store/ui.ts"],
  },
  {
    name: "API Client",
    layer: "application",
    technology: "Typed fetch wrapper",
    responsibilities: [
      "Issue REST calls to /api/*",
      "Build query strings, attach credentials",
      "Throw HttpError on non-2xx",
      "Dispatch window 'aidan:mutation' CustomEvent after mutations",
    ],
    dependencies: ["Worker API"],
    files: ["src/lib/api.ts", "src/lib/types.ts"],
  },
  {
    name: "Hono App",
    layer: "application",
    technology: "Hono@4 on Cloudflare Workers",
    responsibilities: ["Compose middleware", "Mount 14 feature routers", "Health endpoint", "SPA fallback via ASSETS binding"],
    dependencies: ["Middleware", "Feature Routers", "Env Bindings"],
    files: ["worker/index.ts"],
  },
  {
    name: "Middleware",
    layer: "application",
    technology: "Hono built-ins + custom",
    responsibilities: ["Structured logging", "Secure headers", "CORS with credentials", "Auth (session cookie or X-Api-Key)", "Centralised error handling"],
    dependencies: ["D1"],
    files: ["worker/middleware/auth.ts", "worker/middleware/error.ts"],
  },
  {
    name: "Feature Routers",
    layer: "domain",
    technology: "Hono sub-apps",
    responsibilities: [
      "One file per resource: domains, records, versions, approvals, hierarchies, mappings, lineage, imports, quality, integrations, audit, search, settings, stats",
      "Validate input, run SQL on D1, write audit events, return JSON",
    ],
    dependencies: ["D1", "KV", "R2"],
    files: ["worker/routes/*.ts"],
  },
  {
    name: "Worker Lib",
    layer: "domain",
    technology: "TypeScript helpers",
    responsibilities: ["Env typing", "D1 helpers, parameterised query builders"],
    dependencies: [],
    files: ["worker/lib/env.ts", "worker/lib/db.ts"],
  },
  {
    name: "D1 Database",
    layer: "data",
    technology: "Cloudflare D1 (SQLite + FTS5)",
    responsibilities: ["Authoritative store for domains, records, versions, approvals, lineage, quality, audit", "FTS5 virtual table kept in sync by triggers"],
    dependencies: [],
    files: ["worker/db/schema.sql"],
  },
  {
    name: "KV Cache",
    layer: "data",
    technology: "Cloudflare KV",
    responsibilities: ["User preferences (prefs:<userId>)", "Cheap hot-path caches", "Feature flag / settings overlays"],
    dependencies: [],
    files: ["worker/routes/settings.ts"],
  },
  {
    name: "R2 Object Store",
    layer: "data",
    technology: "Cloudflare R2",
    responsibilities: ["Import file blobs", "Generated exports", "Import error reports"],
    dependencies: [],
    files: ["worker/routes/imports.ts"],
  },
  {
    name: "ASSETS binding",
    layer: "platform",
    technology: "Cloudflare Workers static assets",
    responsibilities: ["Serve vite-built dist/", "SPA-style not-found fallback to index.html"],
    dependencies: [],
    files: ["wrangler.toml"],
  },
  {
    name: "Build pipeline",
    layer: "platform",
    technology: "Vite + tsc + Wrangler",
    responsibilities: ["TypeScript build", "Vite bundle into dist/", "Wrangler deploy to Cloudflare"],
    dependencies: [],
    files: ["package.json", "vite.config.ts", "wrangler.toml"],
  },
];

// ---------------------------------------------------------------------------
// Data model catalogue
// ---------------------------------------------------------------------------

export const dataModel: DataEntity[] = [
  {
    id: "Domain",
    name: "Domain",
    table: "domains",
    description: "A logical reference-data set (instruments, counterparties, currencies, etc.).",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "description", type: "TEXT" },
      { name: "icon", type: "TEXT" },
      { name: "owner", type: "TEXT" },
      { name: "primary_key", type: "TEXT", notes: "field key used as record id" },
      { name: "schema_version", type: "INTEGER" },
      { name: "created_at", type: "TEXT" },
      { name: "updated_at", type: "TEXT" },
    ],
    relationships: [
      { to: "DomainField", kind: "1:N" },
      { to: "Record", kind: "1:N" },
      { to: "Hierarchy", kind: "1:N" },
      { to: "MappingSet", kind: "1:N", via: "source_domain_id / target_domain_id" },
      { to: "QualityRule", kind: "1:N" },
      { to: "ImportJob", kind: "1:N" },
    ],
    indexes: [],
  },
  {
    id: "DomainField",
    name: "DomainField",
    table: "domain_fields",
    description: "Schema definition for a domain — typed columns.",
    primaryKey: "(domain_id, field_key)",
    fields: [
      { name: "domain_id", type: "TEXT", notes: "FK domains" },
      { name: "field_key", type: "TEXT" },
      { name: "label", type: "TEXT" },
      { name: "type", type: "TEXT", notes: "string|number|date|boolean|enum|ref|code|json" },
      { name: "required", type: "INTEGER" },
      { name: "enum_values", type: "TEXT", notes: "JSON array" },
      { name: "ref_domain", type: "TEXT" },
      { name: "description", type: "TEXT" },
      { name: "sort_order", type: "INTEGER" },
      { name: "searchable", type: "INTEGER" },
    ],
    relationships: [{ to: "Domain", kind: "1:N" }],
    indexes: [],
  },
  {
    id: "Record",
    name: "Record",
    table: "records",
    description: "A single reference-data row stored as JSON keyed by (domain_id, id).",
    primaryKey: "(domain_id, id)",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "domain_id", type: "TEXT" },
      { name: "version", type: "INTEGER" },
      { name: "status", type: "TEXT", notes: "draft|approved|deprecated" },
      { name: "data", type: "TEXT", notes: "JSON payload" },
      { name: "quality_score", type: "INTEGER" },
      { name: "effective_from", type: "TEXT" },
      { name: "effective_to", type: "TEXT" },
      { name: "tags", type: "TEXT" },
      { name: "created_at", type: "TEXT" },
      { name: "created_by", type: "TEXT" },
      { name: "updated_at", type: "TEXT" },
      { name: "updated_by", type: "TEXT" },
    ],
    relationships: [
      { to: "Domain", kind: "1:N" },
      { to: "RecordVersion", kind: "1:N" },
      { to: "Approval", kind: "1:N" },
      { to: "QualityResult", kind: "1:N" },
    ],
    indexes: ["idx_records_status (domain_id, status)", "idx_records_updated (domain_id, updated_at DESC)"],
  },
  {
    id: "RecordsFTS",
    name: "RecordsFTS",
    table: "records_fts",
    description: "FTS5 virtual table mirroring records; maintained by AFTER INSERT/UPDATE/DELETE triggers.",
    primaryKey: "(domain_id, id) — synthetic",
    fields: [
      { name: "domain_id", type: "UNINDEXED" },
      { name: "id", type: "UNINDEXED" },
      { name: "label", type: "TEXT" },
      { name: "body", type: "TEXT" },
    ],
    relationships: [{ to: "Record", kind: "1:1" }],
    indexes: ["FTS5 tokenize='unicode61'"],
  },
  {
    id: "RecordVersion",
    name: "RecordVersion",
    table: "record_versions",
    description: "Immutable snapshot of a record at version N, plus optional JSON diff.",
    primaryKey: "(domain_id, record_id, version)",
    fields: [
      { name: "domain_id", type: "TEXT" },
      { name: "record_id", type: "TEXT" },
      { name: "version", type: "INTEGER" },
      { name: "data", type: "TEXT" },
      { name: "diff", type: "TEXT" },
      { name: "changed_by", type: "TEXT" },
      { name: "changed_at", type: "TEXT" },
      { name: "comment", type: "TEXT" },
      { name: "approval_id", type: "TEXT", notes: "nullable FK approvals" },
    ],
    relationships: [
      { to: "Record", kind: "1:N" },
      { to: "Approval", kind: "1:1" },
    ],
    indexes: [],
  },
  {
    id: "Approval",
    name: "Approval",
    table: "approvals",
    description: "Maker/checker change request for a record. Two-eyes workflow.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "record_id", type: "TEXT" },
      { name: "domain_id", type: "TEXT" },
      { name: "change_summary", type: "TEXT" },
      { name: "diff", type: "TEXT" },
      { name: "proposed_data", type: "TEXT" },
      { name: "maker", type: "TEXT" },
      { name: "maker_at", type: "TEXT" },
      { name: "checker", type: "TEXT" },
      { name: "checked_at", type: "TEXT" },
      { name: "status", type: "TEXT", notes: "pending|approved|rejected|withdrawn" },
      { name: "priority", type: "TEXT" },
      { name: "impact_count", type: "INTEGER" },
    ],
    relationships: [
      { to: "Record", kind: "1:N" },
      { to: "ApprovalComment", kind: "1:N" },
    ],
    indexes: ["idx_approvals_status (status, maker_at DESC)"],
  },
  {
    id: "ApprovalComment",
    name: "ApprovalComment",
    table: "approval_comments",
    description: "Thread on an approval request.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "approval_id", type: "TEXT" },
      { name: "author", type: "TEXT" },
      { name: "at", type: "TEXT" },
      { name: "body", type: "TEXT" },
    ],
    relationships: [{ to: "Approval", kind: "1:N" }],
    indexes: [],
  },
  {
    id: "Hierarchy",
    name: "Hierarchy",
    table: "hierarchies",
    description: "Named tree (e.g. GICS) over a domain's records.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "domain_id", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "description", type: "TEXT" },
      { name: "created_at", type: "TEXT" },
      { name: "updated_at", type: "TEXT" },
    ],
    relationships: [
      { to: "Domain", kind: "1:N" },
      { to: "HierarchyNode", kind: "1:N" },
    ],
    indexes: [],
  },
  {
    id: "HierarchyNode",
    name: "HierarchyNode",
    table: "hierarchy_nodes",
    description: "Adjacency-list node in a hierarchy.",
    primaryKey: "(hierarchy_id, id)",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "hierarchy_id", type: "TEXT" },
      { name: "parent_id", type: "TEXT", notes: "self-ref" },
      { name: "label", type: "TEXT" },
      { name: "record_count", type: "INTEGER" },
      { name: "badge", type: "TEXT" },
      { name: "sort_order", type: "INTEGER" },
    ],
    relationships: [
      { to: "Hierarchy", kind: "1:N" },
      { to: "HierarchyNode", kind: "1:N", via: "parent_id" },
    ],
    indexes: ["idx_hier_nodes_parent (hierarchy_id, parent_id)"],
  },
  {
    id: "MappingSet",
    name: "MappingSet",
    table: "mapping_sets",
    description: "Named crosswalk between two domains.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "source_domain_id", type: "TEXT" },
      { name: "target_domain_id", type: "TEXT" },
      { name: "description", type: "TEXT" },
      { name: "created_at", type: "TEXT" },
      { name: "last_synced_at", type: "TEXT" },
    ],
    relationships: [
      { to: "Domain", kind: "N:N", via: "source_domain_id / target_domain_id" },
      { to: "MappingItem", kind: "1:N" },
    ],
    indexes: [],
  },
  {
    id: "MappingItem",
    name: "MappingItem",
    table: "mapping_items",
    description: "A single source→target pair within a mapping set.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "set_id", type: "TEXT" },
      { name: "source_id", type: "TEXT" },
      { name: "target_id", type: "TEXT" },
      { name: "confidence", type: "REAL" },
      { name: "verified", type: "INTEGER" },
      { name: "verified_by", type: "TEXT" },
      { name: "notes", type: "TEXT" },
    ],
    relationships: [{ to: "MappingSet", kind: "1:N" }],
    indexes: ["idx_mapping_items_set (set_id, confidence DESC)"],
  },
  {
    id: "LineageNode",
    name: "LineageNode",
    table: "lineage_nodes",
    description: "Node in the data lineage graph (records, systems, feeds, consumers).",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT", notes: "e.g. 'instruments:EQ-AAPL'" },
      { name: "kind", type: "TEXT", notes: "record|system|feed|consumer" },
      { name: "label", type: "TEXT" },
      { name: "domain", type: "TEXT" },
    ],
    relationships: [{ to: "LineageEdge", kind: "1:N" }],
    indexes: [],
  },
  {
    id: "LineageEdge",
    name: "LineageEdge",
    table: "lineage_edges",
    description: "Directed edge between two lineage nodes.",
    primaryKey: "(from_id, to_id, kind)",
    fields: [
      { name: "from_id", type: "TEXT" },
      { name: "to_id", type: "TEXT" },
      { name: "kind", type: "TEXT", notes: "feeds|derives|consumes|references" },
      { name: "confidence", type: "REAL" },
    ],
    relationships: [{ to: "LineageNode", kind: "N:N" }],
    indexes: [],
  },
  {
    id: "QualityRule",
    name: "QualityRule",
    table: "quality_rules",
    description: "Declarative expression evaluated against records in a domain.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "domain_id", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "description", type: "TEXT" },
      { name: "expression", type: "TEXT" },
      { name: "severity", type: "TEXT" },
      { name: "enabled", type: "INTEGER" },
      { name: "created_at", type: "TEXT" },
    ],
    relationships: [
      { to: "Domain", kind: "1:N" },
      { to: "QualityResult", kind: "1:N" },
    ],
    indexes: [],
  },
  {
    id: "QualityResult",
    name: "QualityResult",
    table: "quality_results",
    description: "Last evaluation of a rule against a specific record.",
    primaryKey: "(rule_id, record_id)",
    fields: [
      { name: "rule_id", type: "TEXT" },
      { name: "domain_id", type: "TEXT" },
      { name: "record_id", type: "TEXT" },
      { name: "passed", type: "INTEGER" },
      { name: "message", type: "TEXT" },
      { name: "evaluated_at", type: "TEXT" },
    ],
    relationships: [
      { to: "QualityRule", kind: "1:N" },
      { to: "Record", kind: "1:N" },
    ],
    indexes: ["idx_quality_results_domain (domain_id, passed)"],
  },
  {
    id: "ImportJob",
    name: "ImportJob",
    table: "import_jobs",
    description: "Tracks a CSV/XLSX upload from R2 through to commit.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "domain_id", type: "TEXT" },
      { name: "filename", type: "TEXT" },
      { name: "r2_key", type: "TEXT" },
      { name: "status", type: "TEXT" },
      { name: "column_mapping", type: "TEXT" },
      { name: "rows_total", type: "INTEGER" },
      { name: "rows_ok", type: "INTEGER" },
      { name: "rows_error", type: "INTEGER" },
      { name: "error_report_key", type: "TEXT" },
      { name: "created_by", type: "TEXT" },
      { name: "started_at", type: "TEXT" },
      { name: "finished_at", type: "TEXT" },
      { name: "created_at", type: "TEXT" },
    ],
    relationships: [{ to: "Domain", kind: "1:N" }],
    indexes: [],
  },
  {
    id: "ApiKey",
    name: "ApiKey",
    table: "api_keys",
    description: "Hashed bearer tokens for programmatic access.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "prefix", type: "TEXT" },
      { name: "hash", type: "TEXT", notes: "SHA-256 of secret" },
      { name: "scopes", type: "TEXT" },
      { name: "created_by", type: "TEXT" },
      { name: "created_at", type: "TEXT" },
      { name: "last_used_at", type: "TEXT" },
      { name: "expires_at", type: "TEXT" },
      { name: "revoked", type: "INTEGER" },
    ],
    relationships: [{ to: "User", kind: "1:N" }],
    indexes: [],
  },
  {
    id: "Webhook",
    name: "Webhook",
    table: "webhooks",
    description: "Outbound webhook subscription with HMAC secret.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "url", type: "TEXT" },
      { name: "events", type: "TEXT" },
      { name: "secret_prefix", type: "TEXT" },
      { name: "secret_hash", type: "TEXT" },
      { name: "enabled", type: "INTEGER" },
      { name: "last_delivery_at", type: "TEXT" },
      { name: "last_status", type: "INTEGER" },
      { name: "created_at", type: "TEXT" },
    ],
    relationships: [],
    indexes: [],
  },
  {
    id: "Integration",
    name: "Integration",
    table: "integrations",
    description: "Catalogued external system (inbound, outbound, or bidirectional).",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "name", type: "TEXT" },
      { name: "kind", type: "TEXT", notes: "in|out|bidirectional" },
      { name: "status", type: "TEXT" },
      { name: "domain", type: "TEXT" },
      { name: "tps", type: "INTEGER" },
      { name: "sla", type: "TEXT" },
      { name: "metadata", type: "TEXT" },
    ],
    relationships: [],
    indexes: [],
  },
  {
    id: "AuditEvent",
    name: "AuditEvent",
    table: "audit_events",
    description: "Append-only audit trail for every mutating action.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "at", type: "TEXT" },
      { name: "actor", type: "TEXT" },
      { name: "action", type: "TEXT", notes: "e.g. records.update" },
      { name: "target", type: "TEXT" },
      { name: "diff", type: "TEXT" },
      { name: "ip", type: "TEXT" },
      { name: "user_agent", type: "TEXT" },
    ],
    relationships: [{ to: "User", kind: "1:N", via: "actor" }],
    indexes: ["idx_audit_at (at DESC)", "idx_audit_actor (actor, at DESC)"],
  },
  {
    id: "User",
    name: "User",
    table: "users",
    description: "Authenticated principal.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "email", type: "TEXT", notes: "UNIQUE" },
      { name: "name", type: "TEXT" },
      { name: "avatar_url", type: "TEXT" },
      { name: "created_at", type: "TEXT" },
      { name: "last_login", type: "TEXT" },
    ],
    relationships: [
      { to: "Role", kind: "N:N", via: "user_roles" },
      { to: "ApiKey", kind: "1:N" },
      { to: "AuditEvent", kind: "1:N" },
    ],
    indexes: [],
  },
  {
    id: "Role",
    name: "Role",
    table: "roles",
    description: "Named capability bundle.",
    primaryKey: "id",
    fields: [
      { name: "id", type: "TEXT" },
      { name: "name", type: "TEXT", notes: "UNIQUE" },
      { name: "capabilities", type: "TEXT", notes: "JSON array" },
    ],
    relationships: [{ to: "User", kind: "N:N", via: "user_roles" }],
    indexes: [],
  },
  {
    id: "UserRole",
    name: "UserRole",
    table: "user_roles",
    description: "User <-> Role join.",
    primaryKey: "(user_id, role_id)",
    fields: [
      { name: "user_id", type: "TEXT" },
      { name: "role_id", type: "TEXT" },
    ],
    relationships: [
      { to: "User", kind: "1:N" },
      { to: "Role", kind: "1:N" },
    ],
    indexes: [],
  },
];

// ---------------------------------------------------------------------------
// Tech stack
// ---------------------------------------------------------------------------

export const techStack: TechChoice[] = [
  {
    id: "compute-cloudflare-workers-v8-isolates",
    category: "Compute",
    choice: "Cloudflare Workers (V8 isolates)",
    rationale: "Sub-50ms cold starts globally, no server ops, single deploy artifact serves edge + API.",
    alternatives: ["AWS Lambda + API Gateway", "Fly.io", "Vercel Functions"],
  },
  {
    id: "api-framework-hono-v4",
    category: "API framework",
    choice: "Hono v4",
    rationale: "Tiny (~14kb), idiomatic Workers support, typed Bindings/Variables, fast routing.",
    alternatives: ["itty-router", "Express (incompatible with Workers)", "tRPC"],
  },
  {
    id: "database-cloudflare-d1-sqlite",
    category: "Database",
    choice: "Cloudflare D1 (SQLite)",
    rationale: "Co-located with the worker, FTS5 built in, single binding, free for the data volumes AIDAN handles.",
    alternatives: ["PlanetScale", "Neon Postgres", "Turso (libSQL)"],
  },
  {
    id: "search-sqlite-fts5-records-fts-virtual-table",
    category: "Search",
    choice: "SQLite FTS5 (records_fts virtual table)",
    rationale: "Triggers keep it in sync with records; unicode61 tokenizer handles names; zero extra infra.",
    alternatives: ["Algolia", "Typesense", "Meilisearch", "Cloudflare Vectorize"],
  },
  {
    id: "cache-kv-cloudflare-kv-cache-binding",
    category: "Cache / KV",
    choice: "Cloudflare KV (CACHE binding)",
    rationale: "Eventually-consistent, low-latency reads for prefs and settings.",
    alternatives: ["Redis", "Durable Objects"],
  },
  {
    id: "object-store-cloudflare-r2-files-binding",
    category: "Object store",
    choice: "Cloudflare R2 (FILES binding)",
    rationale: "Zero egress fees, S3-compatible API, ideal for import blobs and exports.",
    alternatives: ["S3", "GCS", "Azure Blob"],
  },
  {
    id: "cdn-edge-cloudflare-custom-domain-rdm-davicity-com-au-worker",
    category: "CDN / Edge",
    choice: "Cloudflare custom domain (rdm.davicity.com.au) + workers.dev fallback",
    rationale: "TLS, DDoS protection and global PoPs are free; SPA assets served via ASSETS binding.",
    alternatives: ["CloudFront", "Fastly"],
  },
  {
    id: "frontend-framework-react-18",
    category: "Frontend framework",
    choice: "React 18",
    rationale: "Industry-standard, mature ecosystem, concurrent rendering for big tables.",
    alternatives: ["Solid", "Svelte", "Vue"],
  },
  {
    id: "routing-react-router-dom-v6-createbrowserrouter",
    category: "Routing",
    choice: "react-router-dom v6 (createBrowserRouter)",
    rationale: "Nested layouts, type-safe params, plays nicely with SPA fallback.",
    alternatives: ["TanStack Router", "wouter"],
  },
  {
    id: "client-state-zustand-4",
    category: "Client state",
    choice: "Zustand 4",
    rationale: "Tiny, no provider hell, supports selector subscriptions for fine-grained renders.",
    alternatives: ["Redux Toolkit", "Jotai", "Recoil"],
  },
  {
    id: "virtualisation-tanstack-react-virtual",
    category: "Virtualisation",
    choice: "@tanstack/react-virtual",
    rationale: "Handles 100k+ row tables without DOM blow-up; headless, easy to style.",
    alternatives: ["react-window", "ag-grid"],
  },
  {
    id: "auth-session-cookie-or-x-api-key-header-custom-middleware",
    category: "Auth",
    choice: "Session cookie or X-Api-Key header (custom middleware)",
    rationale: "Same middleware accepts both user sessions and programmatic keys (hashed SHA-256 in api_keys).",
    alternatives: ["Auth0", "Clerk", "Cloudflare Access"],
  },
  {
    id: "build-vite-5-typescript-5-wrangler",
    category: "Build",
    choice: "Vite 5 + TypeScript 5 + Wrangler",
    rationale: "Fast dev server, ES module output, single npm run build then wrangler deploy.",
    alternatives: ["Webpack", "esbuild", "Rspack"],
  },
  {
    id: "testing-vitest-playwright-axe-playwright",
    category: "Testing",
    choice: "Vitest + Playwright + axe-playwright",
    rationale: "Unit + E2E + accessibility checks share one toolchain.",
    alternatives: ["Jest", "Cypress"],
  },
  {
    id: "observability-cloudflare-workers-observability-enabled-in-wr",
    category: "Observability",
    choice: "Cloudflare Workers Observability (enabled in wrangler.toml)",
    rationale: "Built-in logs/metrics, no external collector.",
    alternatives: ["Datadog", "Honeycomb", "Sentry"],
  },
];

// ---------------------------------------------------------------------------
// API routes
// ---------------------------------------------------------------------------

export const apiRoutes: RouteEntry[] = [
  { method: "GET", path: "/api/health", resource: "health", handler: "worker/index.ts:41", description: "Liveness probe; returns app name + env." },

  // Domains
  { method: "GET", path: "/api/domains", resource: "domains", handler: "worker/routes/domains.ts:8", description: "List all domains." },
  { method: "GET", path: "/api/domains/:id", resource: "domains", handler: "worker/routes/domains.ts:34", description: "Get a single domain." },
  { method: "GET", path: "/api/domains/:id/schema", resource: "domains", handler: "worker/routes/domains.ts:65", description: "Get a domain's field schema." },

  // Records
  { method: "GET", path: "/api/domains/:domainId/records", resource: "records", handler: "worker/routes/records.ts:67", description: "List records (q, status, sort, page, pageSize)." },
  { method: "GET", path: "/api/domains/:domainId/records/export", resource: "records", handler: "worker/routes/records.ts:46", description: "Stream records as CSV/JSON/XLSX." },
  { method: "GET", path: "/api/domains/:domainId/records/:id", resource: "records", handler: "worker/routes/records.ts:104", description: "Get one record." },
  { method: "POST", path: "/api/domains/:domainId/records", resource: "records", handler: "worker/routes/records.ts:112", description: "Create a record." },
  { method: "PATCH", path: "/api/domains/:domainId/records/:id", resource: "records", handler: "worker/routes/records.ts:134", description: "Update a record (creates a version)." },
  { method: "DELETE", path: "/api/domains/:domainId/records/:id", resource: "records", handler: "worker/routes/records.ts:164", description: "Soft-delete a record." },

  // Versions
  { method: "GET", path: "/api/domains/:domainId/records/:recordId/versions", resource: "versions", handler: "worker/routes/versions.ts:22", description: "List versions of a record." },
  { method: "GET", path: "/api/domains/:domainId/records/:recordId/versions/diff", resource: "versions", handler: "worker/routes/versions.ts:31", description: "Diff two versions (a vs b)." },
  { method: "GET", path: "/api/domains/:domainId/records/:recordId/versions/:v", resource: "versions", handler: "worker/routes/versions.ts:47", description: "Get a single version snapshot." },
  { method: "POST", path: "/api/domains/:domainId/records/:recordId/versions/:v/restore", resource: "versions", handler: "worker/routes/versions.ts:56", description: "Restore a record to version v." },

  // Approvals
  { method: "GET", path: "/api/approvals", resource: "approvals", handler: "worker/routes/approvals.ts:32", description: "List approvals (status, priority, page)." },
  { method: "GET", path: "/api/approvals/pending/count", resource: "approvals", handler: "worker/routes/approvals.ts:53", description: "Pending approval count for badge." },
  { method: "GET", path: "/api/approvals/:id", resource: "approvals", handler: "worker/routes/approvals.ts:58", description: "Get one approval (with comments)." },
  { method: "POST", path: "/api/approvals/:id/approve", resource: "approvals", handler: "worker/routes/approvals.ts:85", description: "Approve (checker must differ from maker)." },
  { method: "POST", path: "/api/approvals/:id/reject", resource: "approvals", handler: "worker/routes/approvals.ts:86", description: "Reject with comment." },
  { method: "POST", path: "/api/approvals/:id/withdraw", resource: "approvals", handler: "worker/routes/approvals.ts:87", description: "Maker withdraws own request." },
  { method: "POST", path: "/api/approvals/bulk-approve", resource: "approvals", handler: "worker/routes/approvals.ts:89", description: "Bulk approve by id list." },
  { method: "POST", path: "/api/approvals/:id/comments", resource: "approvals", handler: "worker/routes/approvals.ts:104", description: "Add a comment to an approval." },

  // Hierarchies
  { method: "GET", path: "/api/hierarchies", resource: "hierarchies", handler: "worker/routes/hierarchies.ts:23", description: "List hierarchies (optional domainId)." },
  { method: "GET", path: "/api/hierarchies/:id", resource: "hierarchies", handler: "worker/routes/hierarchies.ts:41", description: "Get a hierarchy tree." },
  { method: "POST", path: "/api/hierarchies/:id/reparent", resource: "hierarchies", handler: "worker/routes/hierarchies.ts:52", description: "Move a node to a new parent." },
  { method: "POST", path: "/api/hierarchies/:id/nodes", resource: "hierarchies", handler: "worker/routes/hierarchies.ts:63", description: "Add a node." },
  { method: "PATCH", path: "/api/hierarchies/:id/nodes/:nodeId", resource: "hierarchies", handler: "worker/routes/hierarchies.ts:73", description: "Rename a node." },
  { method: "DELETE", path: "/api/hierarchies/:id/nodes/:nodeId", resource: "hierarchies", handler: "worker/routes/hierarchies.ts:87", description: "Remove a node." },

  // Mappings
  { method: "GET", path: "/api/mappings", resource: "mappings", handler: "worker/routes/mappings.ts:8", description: "List mapping sets." },
  { method: "POST", path: "/api/mappings", resource: "mappings", handler: "worker/routes/mappings.ts:96", description: "Create a mapping set." },
  { method: "GET", path: "/api/mappings/:setId/items", resource: "mappings", handler: "worker/routes/mappings.ts:24", description: "List mapping items (q, minConfidence, page)." },
  { method: "PATCH", path: "/api/mappings/:setId/items/:itemId", resource: "mappings", handler: "worker/routes/mappings.ts:61", description: "Update target/confidence/notes." },
  { method: "POST", path: "/api/mappings/:setId/items/:itemId/verify", resource: "mappings", handler: "worker/routes/mappings.ts:47", description: "Mark item verified." },
  { method: "POST", path: "/api/mappings/:setId/auto-map", resource: "mappings", handler: "worker/routes/mappings.ts:81", description: "Run heuristic auto-mapper." },

  // Lineage
  { method: "GET", path: "/api/lineage/:domainId/:recordId", resource: "lineage", handler: "worker/routes/lineage.ts:36", description: "Lineage graph for a record (depth <= 5)." },
  { method: "GET", path: "/api/lineage/:domainId/:recordId/impact", resource: "lineage", handler: "worker/routes/lineage.ts:48", description: "Downstream impact preview." },

  // Imports
  { method: "GET", path: "/api/imports", resource: "imports", handler: "worker/routes/imports.ts:17", description: "List import jobs." },
  { method: "GET", path: "/api/imports/:id", resource: "imports", handler: "worker/routes/imports.ts:22", description: "Get one import job." },
  { method: "POST", path: "/api/imports", resource: "imports", handler: "worker/routes/imports.ts:29", description: "Upload a file (multipart) to R2." },
  { method: "POST", path: "/api/imports/:id/confirm", resource: "imports", handler: "worker/routes/imports.ts:49", description: "Confirm column mapping and commit." },
  { method: "POST", path: "/api/imports/:id/cancel", resource: "imports", handler: "worker/routes/imports.ts:66", description: "Cancel an in-flight import." },

  // Quality
  { method: "GET", path: "/api/quality/rules", resource: "quality", handler: "worker/routes/quality.ts:8", description: "List quality rules (optional domainId)." },
  { method: "POST", path: "/api/quality/rules", resource: "quality", handler: "worker/routes/quality.ts:22", description: "Create a quality rule." },
  { method: "PATCH", path: "/api/quality/rules/:id", resource: "quality", handler: "worker/routes/quality.ts:32", description: "Update a quality rule." },
  { method: "POST", path: "/api/quality/rules/:id/run", resource: "quality", handler: "worker/routes/quality.ts:99", description: "Evaluate a single rule." },
  { method: "POST", path: "/api/quality/rules/run-all", resource: "quality", handler: "worker/routes/quality.ts:91", description: "Evaluate every enabled rule." },
  { method: "GET", path: "/api/quality/score/:domainId", resource: "quality", handler: "worker/routes/quality.ts:47", description: "Aggregate quality score for a domain." },
  { method: "GET", path: "/api/quality/violations", resource: "quality", handler: "worker/routes/quality.ts:68", description: "Recent rule failures (optional domain, limit)." },

  // Integrations
  { method: "GET", path: "/api/integrations", resource: "integrations", handler: "worker/routes/integrations.ts:14", description: "Catalogued external systems." },
  { method: "GET", path: "/api/integrations/api-keys", resource: "integrations", handler: "worker/routes/integrations.ts:23", description: "List API keys (metadata only)." },
  { method: "POST", path: "/api/integrations/api-keys", resource: "integrations", handler: "worker/routes/integrations.ts:33", description: "Mint a new API key (returns secret once)." },
  { method: "DELETE", path: "/api/integrations/api-keys/:id", resource: "integrations", handler: "worker/routes/integrations.ts:51", description: "Revoke an API key." },
  { method: "GET", path: "/api/integrations/webhooks", resource: "integrations", handler: "worker/routes/integrations.ts:57", description: "List webhooks." },
  { method: "POST", path: "/api/integrations/webhooks", resource: "integrations", handler: "worker/routes/integrations.ts:65", description: "Create a webhook subscription." },
  { method: "DELETE", path: "/api/integrations/webhooks/:id", resource: "integrations", handler: "worker/routes/integrations.ts:77", description: "Remove a webhook." },
  { method: "POST", path: "/api/integrations/webhooks/:id/test", resource: "integrations", handler: "worker/routes/integrations.ts:82", description: "Send a test ping to the webhook URL." },
  { method: "GET", path: "/api/integrations/sync-logs", resource: "integrations", handler: "worker/routes/integrations.ts:105", description: "Recent integration sync log entries." },

  // Audit
  { method: "GET", path: "/api/audit", resource: "audit", handler: "worker/routes/audit.ts:7", description: "Query audit events (actor, action, from/to, page)." },

  // Search
  { method: "GET", path: "/api/search", resource: "search", handler: "worker/routes/search.ts:7", description: "Global FTS5 search (q, optional domain, limit)." },

  // Settings
  { method: "GET", path: "/api/settings/me", resource: "settings", handler: "worker/routes/settings.ts:7", description: "Current user + preferences (from KV)." },
  { method: "PATCH", path: "/api/settings/me/preferences", resource: "settings", handler: "worker/routes/settings.ts:18", description: "Merge and persist user preferences to KV." },

  // Stats
  { method: "GET", path: "/api/stats/overview", resource: "stats", handler: "worker/routes/stats.ts:7", description: "Dashboard aggregates (totals, per-domain quality, activity, pending)." },
];

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export interface Persona {
  name: string;
  role: string;
  description: string;
}

export interface Kpi {
  label: string;
  value: string;
  hint?: string;
}

export const meta = {
  generatedAt: "2026-05-15T00:00:00Z",
  appName: "AIDAN",
  tagline: "Automated Index for Data Asset Nomenclature",
  source: "reverse-engineered from worker/, src/, schema.sql and wrangler.toml",
  productSummary:
    "AIDAN — the Automated Index for Data Asset Nomenclature — is a reference-data management platform that lets stewards curate, approve, version, search and cross-walk the canonical names and codes a business depends on. It runs entirely on Cloudflare's edge: a React SPA, a Hono API on Workers, D1 (SQLite + FTS5) for relational state, KV for hot prefs, and R2 for import/export blobs.",
  kpis: [
    { label: "API endpoints", value: "60+", hint: "across 14 feature routers" },
    { label: "D1 tables", value: "23", hint: "incl. FTS5 + join tables" },
    { label: "Cloudflare bindings", value: "4", hint: "D1 · KV · R2 · ASSETS" },
    { label: "Diagrams", value: "10", hint: "C4 + flows + ER + state" },
    { label: "SPA stores", value: "5", hint: "Zustand: auth/badges/browse/settings/ui" },
  ] satisfies Kpi[],
  personas: [
    {
      name: "Data Steward",
      role: "Maker / Owner",
      description: "Curates a domain — proposes changes, reviews quality issues, maintains hierarchies and mappings.",
    },
    {
      name: "Checker / Approver",
      role: "Two-eyes reviewer",
      description: "Reviews proposed changes in /approvals, approves or rejects with commentary; cannot approve their own changes.",
    },
    {
      name: "Auditor / Compliance",
      role: "Read-only oversight",
      description: "Inspects the immutable audit log, lineage graphs, and version diffs to satisfy regulatory review.",
    },
    {
      name: "Platform Engineer",
      role: "Integrator",
      description: "Mints API keys, configures webhooks, runs imports, and wires AIDAN into upstream feeds and downstream consumers.",
    },
    {
      name: "Downstream Consumer",
      role: "API client",
      description: "Reads gold-record reference data via REST or receives change events via webhook.",
    },
  ] satisfies Persona[],
} as const;
